import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Trophy,
  Calendar,
  CheckCircle,
  Clock,
  Users,
  Shield,
  Swords,
  RefreshCw,
  TrendingUp,
  Award,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { statsApi, championshipsApi } from '../../lib/api';
import { toast } from 'sonner';

const REFRESH_INTERVAL = 30000; // 30 seconds

const StatCard = ({ icon: Icon, label, value, color, subValue }) => (
  <div className="bg-[#0a0d15] border border-white/10 p-4 flex items-center gap-4">
    <div
      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${color}20` }}
    >
      <Icon size={22} style={{ color }} />
    </div>
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-white font-mono">{value}</p>
      {subValue && <p className="text-xs text-slate-400">{subValue}</p>}
    </div>
  </div>
);

export const StatsTab = ({ isItalian, championships }) => {
  const [globalStats, setGlobalStats] = useState(null);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [topClubs, setTopClubs] = useState([]);
  const [standings, setStandings] = useState([]);
  const [selectedChamp, setSelectedChamp] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchAllStats = useCallback(async () => {
    try {
      const [globalRes, upcomingRes, recentRes, topRes] = await Promise.all([
        statsApi.global(),
        statsApi.upcomingMatches(8),
        statsApi.recentResults(8),
        statsApi.topClubs(10),
      ]);

      setGlobalStats(globalRes.data);
      setUpcomingMatches(upcomingRes.data || []);
      setRecentResults(recentRes.data || []);
      setTopClubs(topRes.data || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStandings = useCallback(async (champId) => {
    if (!champId) {
      setStandings([]);
      return;
    }
    try {
      const res = await championshipsApi.standings(champId);
      setStandings(res.data || []);
    } catch (err) {
      console.error('Error fetching standings:', err);
      setStandings([]);
    }
  }, []);

  useEffect(() => {
    fetchAllStats();
    const interval = setInterval(fetchAllStats, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchAllStats]);

  useEffect(() => {
    const activeChamps = championships.filter(c => c.status === 'in_progress');
    if (activeChamps.length > 0 && !selectedChamp) {
      setSelectedChamp(activeChamps[0].championship_id);
      fetchStandings(activeChamps[0].championship_id);
    }
  }, [championships, selectedChamp, fetchStandings]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-16">
        <div className="animate-spin w-10 h-10 border-2 border-[#00F0FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
      data-testid="stats-tab-content"
    >
      {/* Header + Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 size={22} className="text-[#FFEA00]" />
          <h3 className="font-heading text-lg uppercase tracking-wider text-white">
            {isItalian ? 'Dashboard Statistiche' : 'Statistics Dashboard'}
          </h3>
          {lastUpdate && (
            <span className="text-xs text-slate-500">
              {isItalian ? 'Aggiornato' : 'Updated'}: {lastUpdate.toLocaleTimeString('it-IT')}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="border-white/10 text-slate-400 hover:text-white"
          onClick={() => {
            fetchAllStats();
            if (selectedChamp) fetchStandings(selectedChamp);
            toast.success(isItalian ? 'Statistiche aggiornate!' : 'Stats refreshed!');
          }}
          data-testid="refresh-stats-btn"
        >
          <RefreshCw size={14} className="mr-2" />
          {isItalian ? 'Aggiorna' : 'Refresh'}
        </Button>
      </div>

      {/* Global Stats Cards */}
      {globalStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="global-stats-cards">
          <StatCard icon={Users} label={isItalian ? 'Utenti' : 'Users'} value={globalStats.total_users} color="#00F0FF" />
          <StatCard icon={Shield} label="Club" value={globalStats.total_clubs} color="#2979FF" />
          <StatCard icon={Users} label={isItalian ? 'Giocatori' : 'Players'} value={globalStats.total_players} color="#00E676" />
          <StatCard icon={Swords} label={isItalian ? 'Partite' : 'Matches'} value={globalStats.total_matches} color="#FFEA00" />
          <StatCard icon={CheckCircle} label={isItalian ? 'Completate' : 'Completed'} value={globalStats.completed_matches} color="#D500F9" />
          <StatCard icon={Trophy} label={isItalian ? 'Campionati Attivi' : 'Active Champs'} value={globalStats.active_championships} color="#FF1744" />
        </div>
      )}

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Championship Standings */}
        <div className="bg-[#0a0d15] border border-white/10">
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
                <Trophy size={16} className="text-[#FFEA00]" />
                {isItalian ? 'Classifica Campionato' : 'Championship Standings'}
              </h4>
              <select
                className="bg-[#050508] border border-white/10 text-white text-xs px-2 py-1.5"
                value={selectedChamp}
                onChange={(e) => {
                  setSelectedChamp(e.target.value);
                  fetchStandings(e.target.value);
                }}
                data-testid="standings-championship-select"
              >
                <option value="">{isItalian ? '-- Seleziona --' : '-- Select --'}</option>
                {championships
                  .filter(c => c.status === 'in_progress' || c.status === 'completed')
                  .map(c => (
                    <option key={c.championship_id} value={c.championship_id}>{c.name}</option>
                  ))}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            {standings.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-500 text-xs w-8">#</TableHead>
                    <TableHead className="text-slate-500 text-xs">Club</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">PG</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">V</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">P</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">S</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">GF</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">GS</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">DR</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center font-bold">PT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {standings.map((team, idx) => (
                    <TableRow
                      key={team.club_id}
                      className={`border-white/10 hover:bg-white/5 ${
                        idx === 0 ? 'bg-[#FFEA00]/5' : idx < 3 ? 'bg-[#00E676]/5' : ''
                      }`}
                    >
                      <TableCell className="font-mono text-xs">
                        <span className={`${
                          idx === 0 ? 'text-[#FFEA00] font-bold' : idx < 3 ? 'text-[#00E676]' : 'text-slate-400'
                        }`}>
                          {idx + 1}
                        </span>
                      </TableCell>
                      <TableCell className="text-white text-sm font-medium">{team.club_name}</TableCell>
                      <TableCell className="text-center text-slate-400 text-xs font-mono">{team.played}</TableCell>
                      <TableCell className="text-center text-[#00E676] text-xs font-mono">{team.won}</TableCell>
                      <TableCell className="text-center text-[#FFEA00] text-xs font-mono">{team.drawn}</TableCell>
                      <TableCell className="text-center text-[#FF1744] text-xs font-mono">{team.lost}</TableCell>
                      <TableCell className="text-center text-white text-xs font-mono">{team.goals_for}</TableCell>
                      <TableCell className="text-center text-slate-400 text-xs font-mono">{team.goals_against}</TableCell>
                      <TableCell className="text-center text-xs font-mono">
                        <span className={team.goal_difference > 0 ? 'text-[#00E676]' : team.goal_difference < 0 ? 'text-[#FF1744]' : 'text-slate-400'}>
                          {team.goal_difference > 0 ? '+' : ''}{team.goal_difference}
                        </span>
                      </TableCell>
                      <TableCell className="text-center text-[#FFEA00] text-sm font-bold font-mono">{team.points}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-8 text-center text-slate-500 text-sm">
                {selectedChamp
                  ? (isItalian ? 'Nessuna partita completata' : 'No completed matches')
                  : (isItalian ? 'Seleziona un campionato' : 'Select a championship')}
              </div>
            )}
          </div>
        </div>

        {/* Top Clubs */}
        <div className="bg-[#0a0d15] border border-white/10">
          <div className="p-4 border-b border-white/10">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
              <Award size={16} className="text-[#D500F9]" />
              {isItalian ? 'Top Club (Tutti i Campionati)' : 'Top Clubs (All Championships)'}
            </h4>
          </div>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            {topClubs.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-slate-500 text-xs w-8">#</TableHead>
                    <TableHead className="text-slate-500 text-xs">Club</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">V</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">P</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">S</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">GF</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">GS</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center font-bold">PT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topClubs.map((club, idx) => (
                    <TableRow
                      key={club.club_id}
                      className={`border-white/10 hover:bg-white/5 ${
                        idx === 0 ? 'bg-[#D500F9]/5' : ''
                      }`}
                    >
                      <TableCell className="font-mono text-xs">
                        <span className={idx < 3 ? 'text-[#D500F9] font-bold' : 'text-slate-400'}>
                          {idx + 1}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="text-white text-sm font-medium">{club.club_name}</span>
                          <span className="text-slate-500 text-xs ml-2">({club.championships_count} camp.)</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-[#00E676] text-xs font-mono">{club.total_won}</TableCell>
                      <TableCell className="text-center text-[#FFEA00] text-xs font-mono">{club.total_drawn}</TableCell>
                      <TableCell className="text-center text-[#FF1744] text-xs font-mono">{club.total_lost}</TableCell>
                      <TableCell className="text-center text-white text-xs font-mono">{club.total_goals_for}</TableCell>
                      <TableCell className="text-center text-slate-400 text-xs font-mono">{club.total_goals_against}</TableCell>
                      <TableCell className="text-center text-[#D500F9] text-sm font-bold font-mono">{club.total_points}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-8 text-center text-slate-500 text-sm">
                {isItalian ? 'Nessun dato disponibile' : 'No data available'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming + Recent Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Matches */}
        <div className="bg-[#0a0d15] border border-white/10">
          <div className="p-4 border-b border-white/10">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
              <Calendar size={16} className="text-[#00F0FF]" />
              {isItalian ? 'Prossime Partite' : 'Upcoming Matches'}
            </h4>
          </div>
          <div className="divide-y divide-white/5 max-h-[400px] overflow-y-auto">
            {upcomingMatches.length > 0 ? upcomingMatches.map((m) => (
              <div key={m.match_id} className="p-3 hover:bg-white/5 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-white font-medium truncate">{m.home_club}</span>
                      <span className="text-slate-500 text-xs">vs</span>
                      <span className="text-white font-medium truncate">{m.away_club}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[#00F0FF] text-xs">{m.championship}</span>
                      {m.leg && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          m.leg === 'andata'
                            ? 'bg-[#00F0FF]/10 text-[#00F0FF]'
                            : 'bg-[#D500F9]/10 text-[#D500F9]'
                        }`}>{m.leg}</span>
                      )}
                      {m.match_day && (
                        <span className="text-slate-500 text-xs">G{m.match_day}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <p className="text-xs font-medium text-white">{formatDate(m.match_date)}</p>
                    <p className="text-xs text-[#FFEA00] font-mono">{formatTime(m.match_date)}</p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center text-slate-500 text-sm">
                <Calendar size={32} className="mx-auto mb-2 opacity-30" />
                {isItalian ? 'Nessuna partita programmata' : 'No upcoming matches'}
              </div>
            )}
          </div>
        </div>

        {/* Recent Results */}
        <div className="bg-[#0a0d15] border border-white/10">
          <div className="p-4 border-b border-white/10">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
              <TrendingUp size={16} className="text-[#00E676]" />
              {isItalian ? 'Risultati Recenti' : 'Recent Results'}
            </h4>
          </div>
          <div className="divide-y divide-white/5 max-h-[400px] overflow-y-auto">
            {recentResults.length > 0 ? recentResults.map((m) => {
              const homeWon = m.home_score > m.away_score;
              const awayWon = m.away_score > m.home_score;
              const draw = m.home_score === m.away_score;

              return (
                <div key={m.match_id} className="p-3 hover:bg-white/5 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <span className={`font-medium truncate ${homeWon ? 'text-[#00E676]' : draw ? 'text-[#FFEA00]' : 'text-slate-400'}`}>
                          {m.home_club}
                        </span>
                        <span className="bg-[#050508] px-2.5 py-0.5 rounded text-sm font-mono font-bold border border-white/10 flex-shrink-0">
                          <span className={homeWon ? 'text-[#00E676]' : 'text-white'}>{m.home_score}</span>
                          <span className="text-slate-500 mx-1">-</span>
                          <span className={awayWon ? 'text-[#00E676]' : 'text-white'}>{m.away_score}</span>
                        </span>
                        <span className={`font-medium truncate ${awayWon ? 'text-[#00E676]' : draw ? 'text-[#FFEA00]' : 'text-slate-400'}`}>
                          {m.away_club}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[#00F0FF] text-xs">{m.championship}</span>
                        {m.leg && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            m.leg === 'andata'
                              ? 'bg-[#00F0FF]/10 text-[#00F0FF]'
                              : 'bg-[#D500F9]/10 text-[#D500F9]'
                          }`}>{m.leg}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <p className="text-xs text-slate-400">{formatDate(m.match_date)}</p>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div className="p-8 text-center text-slate-500 text-sm">
                <CheckCircle size={32} className="mx-auto mb-2 opacity-30" />
                {isItalian ? 'Nessun risultato' : 'No results yet'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auto-refresh indicator */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-600">
        <Clock size={12} />
        {isItalian
          ? 'Aggiornamento automatico ogni 30 secondi'
          : 'Auto-refresh every 30 seconds'}
      </div>
    </motion.div>
  );
};
